import { useNodesInitialized } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMeasure } from 'react-use';
import { UseMeasureRect } from 'react-use/lib/useMeasure';

import { useDocumentVisibility } from '@/hooks/useDocumentVisibility';
import { useEffectReducer } from '@/hooks/useEffectReducer';
import { useGetState } from '@/hooks/useGetState';
import { getNodes } from '@/utils/graphqlUtils';
import { reduceEffectReducers } from '@/utils/reducerUtils';

import {
  useCompleteInitialiationEffect,
  useFitViewEffect,
  useRecomputeSectionWidthsEffect,
} from '../effects';
import { EntityDiagram_EntityDiagramFragment } from '../graphql/EntityDiagram.generated';
import { useEntityDiagramRenderReady } from '../hooks/useEntityDiagramRenderReady';
import {
  entityDiagramReducer,
  entityDiagramStateTransformReducer,
} from '../reducers';
import { generateDefaultState } from '../state/generateDefaultState';
import {
  EntityDiagramEffectsMap,
  EntityDiagramInitialStateGetter,
} from '../types';
import { EntityDiagramAction } from '../types/actions';
import { EntityDiagramEffect } from '../types/effects';
import { EntityDiagramState, EntityDiagramStateProps } from '../types/state';
import { EntityDiagramContext } from './entityDiagram.context';

export interface EntityDiagramProviderProps
  extends Pick<
    EntityDiagramStateProps,
    | 'primaryClients'
    | 'entity'
    | 'isTwoClientHousehold'
    | 'selectedPrimaryClientId'
    | 'entityDiagramVariant'
    | 'viewOnlyMode'
    | 'externalDispositions'
    | 'recipientDispositions'
    | 'nonRootRecipientOverride'
    | 'initialSelectedNode'
    | 'bdgDefaultTriggerClientId'
    | 'isBdgEntity'
  > {
  children: JSX.Element;
  presentationMode: boolean;
  isOnDispositionsFormDiagram?: boolean;
  isInHalfWidthContainer?: boolean;
  /**
   * When true, the fit sizes the diagram against the React Flow canvas rather
   * than the measured container (T1-6663). Use where the canvas is scaled to
   * fit a fixed-size slide, so the container measurement doesn't describe the
   * space the nodes actually render into.
   */
  fitToCanvas?: boolean;
  registerDiagramReady?: () => void;
  entityDiagram?: Pick<
    EntityDiagram_EntityDiagramFragment,
    | 'id'
    | 'kind'
    | 'nodeConfigurations'
    | 'hideEntitiesListInGroups'
    | 'visualizationGroups'
  > | null;
  /**
   * Initial value for the "Preview in brand style" toggle. Defaults to false
   * so read-only and presentation renders are unaffected; the edit trowser
   * passes true so the toggle defaults on when editing.
   */
  initialRenderWhiteLabeled?: boolean;
}

export const rootReducer = reduceEffectReducers([
  // Primary reducer that handles incoming actions
  entityDiagramReducer,
  // Reducer that composes state transform functions. Add computations
  // here that are true for every action type and computed based on state
  // or
  // Derived data which is computed from state, but is more in the form of helper
  // boolean flags, object-by-id look up maps, etc.
  entityDiagramStateTransformReducer,
]);

function useEffectsMap({
  containerDimensions,
  fitToCanvas,
}: {
  containerDimensions: UseMeasureRect;
  fitToCanvas: boolean;
}): EntityDiagramEffectsMap {
  const fitViewEffect = useFitViewEffect({
    containerDimensions,
    fitToCanvas,
  });
  const completeInitializationEffect = useCompleteInitialiationEffect();
  const recomputeSectionWidthsEffect = useRecomputeSectionWidthsEffect();

  return useMemo<EntityDiagramEffectsMap>(() => {
    return {
      fitViewEffect,
      completeInitializationEffect,
      recomputeSectionWidthsEffect,
    };
  }, [
    completeInitializationEffect,
    fitViewEffect,
    recomputeSectionWidthsEffect,
  ]);
}

export const EntityDiagramProvider = ({
  children,
  primaryClients,
  isTwoClientHousehold,
  entity,
  entityDiagram,
  presentationMode,
  isOnDispositionsFormDiagram,
  isInHalfWidthContainer,
  fitToCanvas = false,
  viewOnlyMode,
  registerDiagramReady,
  selectedPrimaryClientId,
  entityDiagramVariant,
  externalDispositions,
  recipientDispositions,
  nonRootRecipientOverride,
  initialSelectedNode,
  bdgDefaultTriggerClientId,
  isBdgEntity,
  initialRenderWhiteLabeled = false,
}: EntityDiagramProviderProps) => {
  const isNodesInitialized = useNodesInitialized();
  const visibility = useDocumentVisibility();

  const [entityDiagramContainerRef, containerDimensions] =
    useMeasure<HTMLDivElement>();

  const effectsMap = useEffectsMap({
    containerDimensions,
    fitToCanvas,
  });

  const getInitialState = useCallback<EntityDiagramInitialStateGetter>(
    (_queueEffect) => {
      return {
        ...generateDefaultState({
          primaryClients,
          entity,
          isTwoClientHousehold,
          presentationMode,
          isOnDispositionsFormDiagram,
          isInHalfWidthContainer,
          viewOnlyMode,
          selectedPrimaryClientId,
          entityDiagramVariant,
          entityDiagramId: entityDiagram?.id,
          nodeConfigurations: entityDiagram?.nodeConfigurations ?? [],
          visualizationGroups: getNodes(entityDiagram?.visualizationGroups).map(
            (group) => ({
              id: group.id,
              namespace: group.namespace,
              displayName: group.displayName ?? '',
              description: group.description,
              nodeIds: group.nodeIds,
              hideGroupContents:
                entityDiagram?.nodeConfigurations.find(
                  (configuration) =>
                    configuration.nodeID === `${group.namespace}:${group.id}`
                )?.entityDiagramConfig?.hideGroupContents ?? false,
            })
          ),
          originalVisualizationGroups: getNodes(
            entityDiagram?.visualizationGroups
          ).map((group) => ({
            id: group.id,
            namespace: group.namespace,
            displayName: group.displayName ?? '',
            description: group.description,
            nodeIds: group.nodeIds,
            hideGroupContents:
              entityDiagram?.nodeConfigurations.find(
                (configuration) =>
                  configuration.nodeID === `${group.namespace}:${group.id}`
              )?.entityDiagramConfig?.hideGroupContents ?? false,
          })),
          hideEntitiesListInGroups:
            entityDiagram?.hideEntitiesListInGroups ?? false,
          baseEdges: [],
          externalDispositions,
          recipientDispositions,
          nonRootRecipientOverride,
          initialSelectedNode,
          bdgDefaultTriggerClientId,
          isBdgEntity,
        }),
        renderWhiteLabeled: initialRenderWhiteLabeled,
      };
    },
    [
      primaryClients,
      entity,
      isTwoClientHousehold,
      presentationMode,
      isOnDispositionsFormDiagram,
      isInHalfWidthContainer,
      viewOnlyMode,
      selectedPrimaryClientId,
      entityDiagramVariant,
      entityDiagram?.id,
      entityDiagram?.nodeConfigurations,
      entityDiagram?.visualizationGroups,
      entityDiagram?.hideEntitiesListInGroups,
      externalDispositions,
      recipientDispositions,
      nonRootRecipientOverride,
      initialSelectedNode,
      bdgDefaultTriggerClientId,
      isBdgEntity,
      initialRenderWhiteLabeled,
    ]
  );

  const [state, dispatch] = useEffectReducer<
    EntityDiagramState,
    EntityDiagramAction,
    EntityDiagramEffect
  >(rootReducer, getInitialState, effectsMap);

  const getState = useGetState(state);

  const value = useMemo(
    () => ({ state, dispatch, getState }),
    [dispatch, getState, state]
  );

  const fontsReady = useEntityDiagramRenderReady({
    isInitialized:
      isNodesInitialized && !state.isInitializing && state.opacity === 1,
    onReady: registerDiagramReady,
  });

  // Initial layout: fire once when React Flow has measured nodes AND — if
  // we're on the DP V2 dispositions form — the container has been measured
  // so we can pass its aspect ratio as the stretch target. Other contexts
  // (presentation slides, full-width diagrams) don't need to wait; they
  // dispatch without an aspect and fall back to the fixed constant.
  const { width: containerWidth, height: containerHeight } =
    containerDimensions;
  const hasDispatchedInitRef = useRef(false);
  useEffect(() => {
    if (hasDispatchedInitRef.current) return;
    if (!fontsReady) return;
    if (!isNodesInitialized) return;
    if (!state.isInitializing) return;
    if (isOnDispositionsFormDiagram && (!containerWidth || !containerHeight)) {
      return;
    }

    const targetAspectRatio =
      isOnDispositionsFormDiagram && containerWidth && containerHeight
        ? containerWidth / containerHeight
        : undefined;

    hasDispatchedInitRef.current = true;
    dispatch({ type: 'INITIALIZE_NODE_POSITIONS', targetAspectRatio });
  }, [
    containerHeight,
    containerWidth,
    dispatch,
    fontsReady,
    isNodesInitialized,
    isOnDispositionsFormDiagram,
    state.isInitializing,
  ]);

  // Post-init re-measurement: isNodesInitialized can flip false→true again
  // AFTER our own initial layout (e.g. creating a group node that RF has to
  // re-measure), and we need to capture that by re-running our section
  // bounds sync. This is independent of container aspect — we don't depend
  // on measurement here.
  useEffect(() => {
    if (!isNodesInitialized) return;
    if (state.isInitializing) return;
    dispatch({ type: 'REACTFLOW_NODE_MEASUREMENT_EVENT' });
  }, [dispatch, isNodesInitialized, state.isInitializing]);

  // The flow chart requires the tab to be active to render correctly
  // https://github.com/xyflow/xyflow/issues/2636
  if (visibility !== 'visible' && !isNodesInitialized) return null;

  return (
    <EntityDiagramContext.Provider value={value}>
      <div ref={entityDiagramContainerRef} style={{ height: '100%' }}>
        {children}
      </div>
    </EntityDiagramContext.Provider>
  );
};
